// The wording of every "are you sure?" on the agent's screen (2026-10-27,
// docs/contact-view.md "Confirm prompts"). Each prompt is shown by the shared
// ConfirmDialog.jsx, never the browser's own confirm box: a title, a short
// body, and two buttons that say what they do (no OK / Cancel). cancelLabel
// is the default (left, focused, also Escape); confirmLabel is the primary.
// Pure: no React, so node --test covers it.

import { etDateTime, resolutionReplyPrompt } from '@cxhub/shared'

export const KEEP_SAVED_WORK_LABEL = 'Keep my saved work'
// The keep button on a contact with no saved work restored (never parked,
// nothing autosaved and reopened): the work is on screen, not saved
// (2026-10-28).
export const KEEP_MY_WORK_LABEL = 'Keep my work'
export const USE_NEW_SUGGESTION_LABEL = 'Use new suggestion'

// The customer message a new suggestion answers, when it is a different one
// from the suggestion on screen: { id, at } (at may be null), else null.
export function newCustomerMessageFor(current, fresh, messages = []) {
  const id = fresh?.triggerMessageId ?? null
  if (!id || !current || id === current.triggerMessageId) return null
  const m = (messages ?? []).find((x) => x?.id === id) ?? null
  return { id, at: m ? (m.receivedAt ?? m.sentAt ?? m.createdAt ?? null) : null }
}

// "A new suggestion arrived while you have edits": a manual Regenerate, a new
// customer message, Load new draft. Keep leaves the reply and the steps as
// they are; Use applies the new suggestion. savedWork: saved work was
// restored on this contact (a park, or an autosave reopened): Keep reads
// "Keep my saved work"; otherwise "Keep my work" (2026-10-28).
export function newSuggestionPrompt({ current = null, fresh = null, messages = [], savedWork = false } = {}) {
  const msg = newCustomerMessageFor(current, fresh, messages)
  const when = msg?.at ? etDateTime(msg.at) : ''
  return {
    title: 'A new suggestion is ready',
    body: "You've made changes to this contact. Keep your work, or replace it with the new suggestion?",
    extra: msg ? (when ? `A new customer message came in at ${when}.` : 'A new customer message came in.') : null,
    cancelLabel: savedWork ? KEEP_SAVED_WORK_LABEL : KEEP_MY_WORK_LABEL,
    confirmLabel: USE_NEW_SUGGESTION_LABEL,
    testId: 'confirm-new-suggestion',
  }
}

// A task's path switch that would drop edits on the steps being hidden.
export const SWITCH_TASK_PATH_PROMPT = {
  title: 'Switch the path?',
  body: 'You edited the steps or the reply on this path. Switching puts them back to their defaults and loads the other path\'s template.',
  cancelLabel: 'Keep this path',
  confirmLabel: 'Switch path (your changes will be lost)',
  testId: 'confirm-switch-task-path',
}

// The resolution switch (2026-10-27, docs/contact-view.md "Replacement or
// refund on the step"): switching between replacement and refund after the
// person edited the reply. title: the new path's template title, when known.
export function replaceReplyPrompt(title = null) {
  return {
    title: 'Replace your reply?',
    body: `${resolutionReplyPrompt(title)} Your edits to the reply are replaced.`,
    cancelLabel: 'Keep my reply',
    confirmLabel: 'Use the new template',
    testId: 'confirm-replace-reply',
  }
}

// The reply after Process (2026-10-29, houseTemplateReply.js): the processed
// step's house template, filled with its results, over a reply the person
// edited. title: the template's title, when known.
export function houseTemplateReplyPrompt(title = null) {
  return {
    title: 'Replace your reply?',
    body: `The step is done. ${title ? `The "${title}"` : 'Its'} template is filled with the results. Using it replaces your edits to the reply.`,
    cancelLabel: 'Keep my reply',
    confirmLabel: title ? `Use the ${title} reply` : 'Use the template reply',
    testId: 'confirm-house-template-reply',
  }
}

// Every "Replace your reply?" here asks only when the person edited the
// reply since CXHub last put text in the box (2026-11-02, Jenn,
// replyEdited.js); an untouched reply is replaced without asking.

// A replacement or refund step switched to a reason code that needs no $0
// RMA (2026-11-03, Jenn): its return label box is gone and no label is sent.
// The "label will follow" line the box put in the reply comes out without
// asking when the reply is untouched; an edited reply asks first.
export const REMOVE_LABEL_LINE_PROMPT = {
  title: 'Remove the return label line?',
  body: 'This reason code needs no $0 RMA, so no return label will be sent. Remove "Your return label will follow..." from your reply? Your other edits stay.',
  cancelLabel: 'Keep my reply',
  confirmLabel: 'Remove the line',
  testId: 'confirm-remove-label-line',
}

// Other Actions' "Paste this into the draft reply" (2026-10-30,
// ManualStepReply.jsx) over a reply the person edited. An empty or
// signature-only box, or the untouched AI / agent draft or an earlier
// paste, pastes without asking (2026-11-02).
export const PASTE_MANUAL_REPLY_PROMPT = {
  title: 'Replace your reply?',
  body: 'You edited the reply. Replace it with this one?',
  cancelLabel: 'Cancel',
  confirmLabel: 'Replace',
  testId: 'confirm-paste-manual-reply',
}

// A Return Status stage chosen over a reply the person edited (2026-10-29;
// since 2026-11-02 only an edited reply, whoever drafted it): the stage's
// template replaces it only if the person says so.
export function stageTemplatePrompt(title = null) {
  return {
    title: 'Replace your reply?',
    body: `Use the ${title ? `"${title}"` : 'stage\'s'} template in the reply box? Your edits to the reply are replaced. Your choice is saved either way.`,
    cancelLabel: 'Keep my reply',
    confirmLabel: 'Use the template',
    testId: 'confirm-stage-template',
  }
}

// The return stage's date fills the template into an edited reply.
export const FILL_DATE_PROMPT = {
  title: 'Replace your reply?',
  body: 'Filling the date puts the template, with the date, into the reply box. Your edits to the reply are replaced.',
  cancelLabel: 'Keep my reply',
  confirmLabel: 'Fill the date',
  testId: 'confirm-fill-date',
}

// App.jsx: switching contacts (a click, back / forward) with an edited reply.
export const SWITCH_CONTACT_PROMPT = {
  title: 'Leave this contact?',
  body: "You've changed the reply on this contact. Switching discards your changes.",
  cancelLabel: 'Stay on this contact',
  confirmLabel: 'Discard my changes and switch contacts',
  testId: 'confirm-switch-contact',
}

// App.jsx: closing a contact's tab with an edited reply.
export const CLOSE_CONTACT_PROMPT = {
  title: 'Stop viewing this contact?',
  body: "You've changed the reply on this contact. Closing it discards your changes.",
  cancelLabel: 'Stay on this contact',
  confirmLabel: 'Discard my changes and close',
  testId: 'confirm-close-contact',
}

// App.jsx: leaving the New page with an unsent email.
export const DISCARD_EMAIL_PROMPT = {
  title: 'Discard this email?',
  body: "The email you're writing isn't saved. Leaving this page discards it.",
  cancelLabel: 'Keep writing',
  confirmLabel: 'Discard email',
  confirmColor: 'error',
  testId: 'confirm-discard-email',
}

// SpamRulesView.jsx (admin): deleting a rule, and turning one on or off.
export function deleteSpamRulePrompt(name) {
  return {
    title: 'Delete this spam rule?',
    body: `Deleting "${name}" cannot be undone.`,
    cancelLabel: 'Keep the rule',
    confirmLabel: 'Delete rule',
    confirmColor: 'error',
    testId: 'confirm-delete-spam-rule',
  }
}

export function toggleSpamRulePrompt(name, isActive) {
  return isActive
    ? { title: 'Disable this spam rule?', body: `"${name}" stops matching new contacts until it is enabled again.`, cancelLabel: 'Keep it on', confirmLabel: 'Disable rule', testId: 'confirm-toggle-spam-rule' }
    : { title: 'Enable this spam rule?', body: `"${name}" starts matching new contacts.`, cancelLabel: 'Leave it off', confirmLabel: 'Enable rule', testId: 'confirm-toggle-spam-rule' }
}
